"use client";

import Image from "next/image";
import { ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useModal } from "@/app/providers/modal-provider";
import HomeSection from "@/components/marketing/HomeSection";
import CustomModal from "@/components/shared/CustomModal";

const features = [
  { key: "IMPORT", position: "lg:col-start-1 lg:row-start-1" },
  { key: "JOBS", position: "lg:col-start-3 lg:row-start-1" },
  { key: "TAILOR", position: "lg:col-start-1 lg:row-start-2" },
  { key: "APPLY", position: "lg:col-start-3 lg:row-start-2" },
] as const;

const demoSource = "/assets/oficial-demo-landfast.mp4";

export function LandfastInActionSection() {
  const { t } = useTranslation();
  const { setOpen } = useModal();

  const openDemo = () => {
    setOpen(
      <CustomModal
        title={t("HOME_ACTION_DEMO_TITLE")}
        subheading={t("HOME_DEMO_SUBHEADING_TEXT")}
      >
        <div className="aspect-video overflow-hidden rounded-xl bg-slate-950">
          <video src={demoSource} controls playsInline preload="metadata" className="h-full w-full">
            <a href={demoSource}>{t("HOME_ACTION_VIDEO_FALLBACK")}</a>
          </video>
        </div>
        <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">{t("HOME_DEMO_TIP_TEXT")}</p>
      </CustomModal>
    );
  };

  return (
    <section
      id="landfast-in-action"
      aria-labelledby="landfast-in-action-title"
      className="relative overflow-hidden rounded-[2.25rem] border border-white/45 bg-white/[0.62] px-5 py-8 text-slate-900 shadow-[0_34px_110px_-68px_rgba(15,23,42,0.72)] backdrop-blur-2xl dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-100 sm:px-8 sm:py-10 lg:px-12 lg:py-12"
    >
      <div className="pb-12 sm:pb-16">
      <HomeSection
        align="left"
        badgeText="HOME_ACTION_BADGE"
        title="HOME_ACTION_TITLE"
        titleSize="compact"
        subtitle="HOME_ACTION_DESC"
        subtitlePosition="aside"
        titleId="landfast-in-action-title"
        action={
            <button
              type="button"
              onClick={openDemo}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-indigo-500 via-sky-500 to-emerald-400 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/30 transition hover:opacity-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-sky-300 sm:px-5 sm:py-2.5"
            >
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/20 text-white transition group-hover:scale-105 sm:h-7 sm:w-7"><ArrowUpRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={2.2} aria-hidden="true" /></span>
              {t("HOME_ACTION_CTA")}
            </button>
        }
      />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_1.35fr_1fr] lg:grid-rows-2">
        {features.map(({ key, position }, index) => (
          <article key={key} className={`min-w-0 rounded-xl border border-white/60 bg-white/[0.55] p-6 backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.05] sm:p-7 ${position}`}>
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">[{String(index + 1).padStart(2, "0")}]</span>
            <h3 className="mt-6 text-2xl leading-[1.15] tracking-[-0.035em] text-slate-900 dark:text-white lg:text-[1.75rem]">{t(`HOME_ACTION_${key}_TITLE`)}</h3>
            <p className="mt-5 text-base leading-[1.4] tracking-[-0.015em] text-slate-600 dark:text-slate-300 lg:text-lg">{t(`HOME_ACTION_${key}_DESC`)}</p>
          </article>
        ))}
        <figure className="relative order-first min-h-[360px] overflow-hidden rounded-xl bg-gradient-to-br from-cyan-300 via-teal-200 to-lime-300 sm:col-span-2 sm:min-h-[420px] lg:order-none lg:col-span-1 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:min-h-[560px]">
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[repeating-radial-gradient(ellipse_at_90%_5%,transparent_0,transparent_19px,rgba(255,255,255,0.5)_20px,transparent_21px)] opacity-40" />
          <div className="absolute left-[12%] top-[15%] w-[130%] overflow-hidden rounded-xl border border-white/80 bg-white shadow-[0_25px_70px_-30px_rgba(15,23,42,0.35)]">
            <Image
              src="/images/landfast-in-action.jpg"
              alt={t("HOME_ACTION_PREVIEW_ALT")}
              width={1200}
              height={1280}
              sizes="(min-width: 1024px) 800px, (min-width: 640px) 1000px, 600px"
              className="h-auto w-full"
            />
          </div>
        </figure>
      </div>
    </section>
  );
}
